import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const client = axios.create({ baseURL: API_BASE });

// ── Auth token helpers ────────────────────────────────────────────────────────
// Access token lives in memory only — never touches localStorage.
// Refresh token lives in an HttpOnly cookie set by the backend (JS can't read it).

let _accessToken = null;

export function getToken() {
  return _accessToken;
}

export function setToken(token) {
  _accessToken = token;
}

export function clearToken() {
  _accessToken = null;
  localStorage.removeItem('auth_user');
}

export function getStoredUser() {
  try {
    return JSON.parse(localStorage.getItem('auth_user') || 'null');
  } catch {
    return null;
  }
}

export function setStoredUser(user) {
  localStorage.setItem('auth_user', JSON.stringify(user));
}

// ── Refresh logic (shared by axios interceptor and streamMessage) ─────────────

let _refreshPromise = null;

async function tryRefresh() {
  if (_refreshPromise) return _refreshPromise;

  // POST with credentials so the browser sends the HttpOnly refresh_token cookie.
  _refreshPromise = axios
    .post(`${API_BASE}/auth/refresh`, {}, { withCredentials: true })
    .then((res) => {
      setToken(res.data.access_token);
      return true;
    })
    .catch(() => false)
    .finally(() => {
      _refreshPromise = null;
    });

  return _refreshPromise;
}

function forceLogout() {
  clearToken();
  window.location.reload();
}

// ── Axios interceptors ────────────────────────────────────────────────────────

client.interceptors.request.use((config) => {
  const token = getToken();
  if (token) config.headers['Authorization'] = `Bearer ${token}`;
  return config;
});

client.interceptors.response.use(
  (res) => res,
  async (err) => {
    const original = err.config;
    if (err.response?.status === 401 && !original._refreshed) {
      original._refreshed = true;
      const ok = await tryRefresh();
      if (ok) {
        original.headers['Authorization'] = `Bearer ${getToken()}`;
        return client(original);
      }
      forceLogout();
    }
    return Promise.reject(err);
  }
);

// ── Auth bootstrap (call once on app load) ────────────────────────────────────
// Returns true if the session is valid (token already present or refresh OK).
// Returns false if the refresh token is missing/expired — caller should log out.

export async function bootstrapAuth() {
  if (_accessToken) return true;
  return tryRefresh();
}

// ── Auth ──────────────────────────────────────────────────────────────────────

export async function login(username, password) {
  // withCredentials so the browser stores the HttpOnly refresh_token cookie.
  const res = await client.post('/auth/login', { username, password }, { withCredentials: true });
  const { access_token, user_id, username: uname } = res.data;
  setToken(access_token);
  setStoredUser({ user_id, username: uname });
  return res.data;
}

export async function logout() {
  clearToken();
  // withCredentials so the browser sends the cookie; backend revokes + clears it.
  axios.post(`${API_BASE}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
}


// ── Config ────────────────────────────────────────────────────────────────────
 
export const getConfig = () =>
  client.get('/config').then(r => r.data);
 
export const getTools = () =>
  client.get('/config/tools').then(r => r.data.tools ?? []);
 
export const getDefaultSystemPrompt = () =>
  client.get('/default-system-prompt').then(r => r.data.system_prompt);
 
/**
 * Tell the backend to switch its active LLM model.
 * The model must be in the server's LLM_MODELS list.
 */
export const setActiveModel = (model) =>
  client.post('/config/model', { model }).then(r => r.data);


// ── Sessions ──────────────────────────────────────────────────────────────────

export const getSessions = () =>
  client.get('/sessions').then(r => {
    const raw = r.data.sessions ?? [];
    return raw.map(s => (typeof s === 'string' ? { id: s, name: null } : s));
  });

export const getSession = (sid) =>
  client.get(`/sessions/${sid}`).then(r => r.data.history);

export const renameSession = (sid, name) =>
  client.patch(`/sessions/${sid}/name`, { name });

export const updateSessionSystemPrompt = (sid, systemPrompt) =>
  client.patch(`/sessions/${sid}/system-prompt`, { system_prompt: systemPrompt ?? null });

export const updateSessionSettings = (sid, { saveToKbDefault, toolConfig }) =>
  client.patch(`/sessions/${sid}/settings`, {
    save_to_kb_default: saveToKbDefault,
    tool_config: toolConfig ?? null,
  }).then(r => r.data);

export const deleteSession = (sid) =>
  client.delete(`/sessions/${sid}`);

export const bulkDeleteSessions = (ids) =>
  client.delete('/sessions', { data: { ids } }).then(r => r.data);

// ── Messages ──────────────────────────────────────────────────────────────────

export const pinMessage = (sid, role, content) =>
  client.post(`/sessions/${sid}/messages`, { role, content });

export const unpinMessage = (sid, role, content) =>
  client.delete(`/sessions/${sid}/messages`, { data: { role, content } });

export const clearHistory = (sid) =>
  client.delete(`/sessions/${sid}/history`).then(r => r.data);

// ── Session-scoped prompts ────────────────────────────────────────────────────

export const getSessionPrompts = (sid) =>
  client.get(`/sessions/${sid}/prompts`).then(r => r.data.prompts ?? []);

export const createSessionPrompt = (sid, content) =>
  client.post(`/sessions/${sid}/prompts`, { content }).then(r => r.data.prompt);

export const deleteSessionPrompt = (sid, promptId) =>
  client.delete(`/sessions/${sid}/prompts/${promptId}`);

// ── Shared helper: build content blocks for a message that has attachments ────
//
// Used by both sendMessage and streamMessage so that context history always
// carries the full attachment content on every subsequent turn.
//
// Images   → inline base64 image_url block (model can see the pixels).
// All else → decoded text block prefixed with [File: <n>].
//            Falls back to the filename-only stub if decoding fails (e.g. a
//            corrupt or non-UTF-8 binary that slipped past the file-type check).
//
// Pass MAX_TEXT_BYTES to cap how many bytes of text are inlined per file.
// At 50 000 bytes (~50 KB) a 1 MB PDF stays well under typical token limits
// while still conveying its content.  Set to Infinity to inline everything.

const MAX_TEXT_BYTES = 50_000;

function buildContentBlocks(m) {
  if (!m.attachments || m.attachments.length === 0) {
    return { role: m.role, content: m.content };
  }

  const blocks = [];

  for (const att of m.attachments) {
    if (att.media_type.startsWith('image/')) {
      blocks.push({
        type:      'image_url',
        image_url: { url: `data:${att.media_type};base64,${att.data}` },
      });
    } else {
      try {
        const raw  = atob(att.data);
        const text = raw.length > MAX_TEXT_BYTES
          ? raw.slice(0, MAX_TEXT_BYTES) + `\n… [truncated at ${MAX_TEXT_BYTES} bytes]`
          : raw;
        blocks.push({ type: 'text', text: `[File: ${att.filename}]\n${text}` });
      } catch {
        // Binary or corrupt data — fall back to a name-only stub so the model
        // at least knows a file was present.
        blocks.push({ type: 'text', text: `[File: ${att.filename}]` });
      }
    }
  }

  if (m.content) blocks.push({ type: 'text', text: m.content });

  return { role: m.role, content: blocks };
}

// ── Chat (non-streaming) ──────────────────────────────────────────────────────

export const sendMessage = (message, thread_id, save_to_kb = false, context = [], systemPrompt = null, toolConfig = null) =>
  client.post('/chat', {
    message,
    thread_id,
    save_to_kb,
    system_prompt: systemPrompt || null,
    tool_config:   toolConfig  || null,
    context: context
      .filter(m => m.role === 'user' || m.role === 'assistant')
      .map(buildContentBlocks),
  }).then(r => r.data);

// ── Chat (streaming) ──────────────────────────────────────────────────────────

export async function streamMessage(
  message,
  thread_id,
  save_to_kb   = false,
  context      = [],
  systemPrompt = null,
  toolConfig   = null,
  attachments  = null,
) {
  const bodyJson = JSON.stringify({
    message,
    thread_id,
    save_to_kb,
    system_prompt: systemPrompt  || null,
    tool_config:   toolConfig    || null,
    attachments:   attachments   || null,
    context: context
      .filter(m => m.role === 'user' || m.role === 'assistant')
      .map(buildContentBlocks),
  });

  const doFetch = () => {
    const token = getToken();
    return fetch(`${API_BASE}/chat/stream`, {
      method:      'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      },
      body: bodyJson,
    });
  };

  let response = await doFetch();

  if (response.status === 401) {
    const ok = await tryRefresh();
    if (ok) {
      response = await doFetch();
    } else {
      forceLogout();
      throw new Error('Unauthorized');
    }
  }

  if (!response.ok) {
    let detail = `HTTP ${response.status}`;
    try {
      const err = await response.json();
      if (err.detail) detail = err.detail;
    } catch { /* ignore */ }
    throw new Error(detail);
  }

  return response.body;
}

// ── RAG: Documents ────────────────────────────────────────────────────────────

export const getDocuments = (sid) =>
  client.get(`/sessions/${sid}/documents`).then(r => r.data.documents ?? []);

export const uploadDocument = (sid, file, onUploadProgress) => {
  const form = new FormData();
  form.append('file', file);
  return client.post(`/sessions/${sid}/documents`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress,
  }).then(r => r.data);
};

export const deleteDocument = (sid, docId) =>
  client.delete(`/sessions/${sid}/documents/${docId}`);

export const ingestUrl = (sid, url) =>
  client.post(`/sessions/${sid}/urls`, { url }).then(r => r.data);

export const reassessDocument = (sid, docId) =>
  client.post(`/sessions/${sid}/documents/${docId}/reassess`).then(r => r.data);